import { useRef, FC } from "react";
import { ArrowNarrowRight } from "untitledui-js-base";
import { cx } from "shared/utils";
import { debounce } from "../../../constants";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import FailedIcon from "shared/icons/status-icons/fail-icon.svg?react";

type Props = {
  ranges: number[];
  setRanges: (ranges: number[]) => void;
  disabled?: boolean;
};

export const RangeSlider: FC<Props> = ({ ranges, setRanges, disabled }) => {
  const sliderRef = useRef(null);

  const handleMouseMove = (e, index) => {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!sliderRef.current) return;

    const rect = (
      sliderRef.current as {
        getBoundingClientRect: () => { width: number; left: number };
      }
    ).getBoundingClientRect();
    const max = rect.width;
    const xPos = e.clientX - rect.left;
    const percentage = (xPos / max) * 10;
    let newValue = parseFloat((Math.round(percentage * 10) / 10).toFixed(1));

    if (index === 0) newValue = Math.max(1, Math.min(newValue, ranges[1] - 1));
    else newValue = Math.min(Math.max(newValue, ranges[0] + 1), 9);

    const newRanges = [...ranges];
    newRanges[index] = newValue;
    setRanges(newRanges);
  };

  const startMouseMove = (index) => {
    const debouncedHandleMouseMove = debounce(
      (e) => handleMouseMove(e, index),
      10
    );
    const move = (e) => debouncedHandleMouseMove(e);

    const stop = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", stop);
    };

    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", stop);
  };

  const calculateBackground = () => {
    const lowColor = disabled ? "#FAA8A7" : "#F4423F";
    const midColor = disabled ? "#FAD48D" : "#F4A107";
    const highColor = disabled ? "#B3EDA4" : "#5AD83A";

    const startPercent = (ranges[0] / 10) * 100;
    const endPercent = (ranges[1] / 10) * 100;

    return `linear-gradient(
      to right, 
      ${lowColor} 0%, 
      ${lowColor} ${startPercent}%, 
      ${midColor} ${startPercent}%, 
      ${midColor} ${endPercent}%, 
      ${highColor} ${endPercent}%, 
      ${highColor} 100%
    )`;
  };

  return (
    <div className="w-full">
      <div
        ref={sliderRef}
        className={cx(
          "relative h-[14px] rounded transition-all",
          disabled ? "" : "cursor-pointer"
        )}
        style={{
          background: calculateBackground()
        }}
      >
        <div className="absolute -top-12 right-0 rounded bg-alpha-80 p-1 text-base font-medium text-content-600">
          10
        </div>
        <div className="absolute -top-12 rounded bg-alpha-80 p-1 text-base font-medium text-content-600">
          0
        </div>
        {ranges.map((range, index) => (
          <div
            key={index}
            className="relative"
            onMouseDown={() => (disabled ? null : startMouseMove(index))}
          >
            <div
              className={cx(
                `absolute -top-2 h-8 w-2.5 rounded-[35px] border border-alpha-200 bg-background-1000 shadow-sm`,
                disabled ? "" : "cursor-grab"
              )}
              style={{ left: `calc(${(range / 10) * 100}% - 4px)` }}
            />
            <div
              className="absolute -top-12 rounded bg-alpha-80 p-1"
              style={{ left: `calc(${(range / 10) * 100}% - 15px)` }}
            >
              <span className="text-base font-medium text-content-600">
                {range.toFixed(1)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <div className="mb-3 flex items-center gap-2">
          <ScoreRange range1="0" range2={ranges[0].toFixed(1)} />
          <ArrowNarrowRight size="20" className="stroke-content-100" />
          <FailedIcon className="text-danger-500 size-4" />
          <p className="text-sm font-medium text-content-300">
            Recommend Decline
          </p>
        </div>
        <div className="mb-3 flex items-center gap-2">
          <ScoreRange
            range1={(ranges[0] + 0.1).toFixed(1)}
            range2={ranges[1].toFixed(1)}
          />
          <ArrowNarrowRight size="20" className="stroke-content-100" />
          <SuccessIcon className="text-warning-500 size-4" />
          <p className="text-sm font-medium text-content-300">
            Recommend Conditional Approval
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ScoreRange range1={(ranges[1] + 0.1).toFixed(1)} range2="10" />
          <ArrowNarrowRight size="20" className="stroke-content-100" />
          <SuccessIcon className="text-success-600 size-4" />
          <p className="text-sm font-medium text-content-300">
            Recommend Approval
          </p>
        </div>
      </div>
    </div>
  );
};

const ScoreRange = ({ range1, range2 }: { range1: string; range2: string }) => {
  return (
    <p className="flex items-center gap-2 text-sm font-medium text-content-300">
      If score range
      <div className="flex h-[30px] w-[36px] items-center justify-center rounded-lg bg-alpha-40">
        <span className="text-sm font-medium text-content-400">{range1}</span>
      </div>
      to
      <div className="flex h-[30px] w-[36px] items-center justify-center rounded-lg bg-alpha-40">
        <span className="text-sm font-medium text-content-400">{range2}</span>
      </div>
    </p>
  );
};
